import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Check, ChevronDown, Globe, Loader2, Search } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ResponsivePopover, ResponsivePopoverContent, ResponsivePopoverTrigger } from "../ui/responsive-popover";
import { BASE, adminError, canViewPlatform, isSessionRequired, pharmacyPath } from "../../lib/jamoraAdmin";
import { useAuth } from "../../contexts/AuthContext";
import { Pill } from "./AdminBits";
import { useAdminSession } from "./SessionProvider";

// « Plateforme » or one pharmacy: the choice lives in the URL (the shell reads it back), so this component only navigates.
export default function ContextSelector({ context, label, choosing = false }) {
  const { user } = useAuth();
  const session = useAdminSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [state, setState] = useState({ items: [], cursor: null, phase: "idle", error: "" });
  const seq = useRef(0);

  const load = useCallback(async (term, cursor) => {
    const mine = ++seq.current;
    setState((s) => ({ ...s, phase: "loading", error: "", items: cursor ? s.items : [] }));
    try {
      const { data } = await api.get("/jamora-admin/pharmacy-options", { params: { limit: 30, ...(term ? { search: term } : {}), ...(cursor ? { cursor } : {}) } });
      if (mine !== seq.current) return;
      setState((s) => ({ items: cursor ? [...s.items, ...data.items] : data.items, cursor: data.next_cursor, phase: "ready", error: "" }));
    } catch (e) {
      if (mine !== seq.current) return;
      if (isSessionRequired(e)) { session?.expire(); return; }
      setState((s) => ({ ...s, phase: "error", error: adminError(e, "Impossible de charger les pharmacies.") }));
    }
  }, [session]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => load(search.trim(), null), search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [open, search, load]);

  const choose = (path) => { setOpen(false); navigate(path); };
  const platform = canViewPlatform(user);
  const inPharmacy = context.kind === "pharmacie";

  return (
    <ResponsivePopover open={open} onOpenChange={setOpen}>
      <ResponsivePopoverTrigger asChild>
        <Button variant="outline" className="h-10 min-w-0 max-w-full justify-between gap-2" aria-label="Changer de contexte" data-testid="context-trigger">
          {inPharmacy ? <Building2 size={16} aria-hidden="true" /> : <Globe size={16} aria-hidden="true" />}
          <span className="truncate">{label}</span>
          <ChevronDown size={14} className="shrink-0 text-slate-400" aria-hidden="true" />
        </Button>
      </ResponsivePopoverTrigger>
      <ResponsivePopoverContent title="Choisir un contexte" align="start" className="w-80 p-0">
        <div className="p-2 border-b border-slate-100">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Chercher une pharmacie…" className="pl-9 h-9" aria-label="Chercher une pharmacie" data-testid="context-search" />
          </div>
        </div>
        <ul className="max-h-72 overflow-y-auto py-1" data-testid="context-list">
          {platform && (
            <li>
              <button type="button" onClick={() => choose(BASE)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50" data-testid="context-platform">
                <Globe size={15} className="text-slate-500" aria-hidden="true" /><span className="flex-1 font-medium text-slate-900">Plateforme Jamora</span>
                {!inPharmacy && !choosing && <Check size={15} className="text-jamora" aria-label="Contexte actuel" />}
              </button>
            </li>
          )}
          {state.items.map((p) => (
            <li key={p.schema}>
              <button type="button" onClick={() => choose(pharmacyPath(p.schema))} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50" data-testid={`context-pharmacy-${p.schema}`}>
                <Building2 size={15} className="text-slate-500 shrink-0" aria-hidden="true" />
                <span className="flex-1 min-w-0 truncate text-slate-900">{p.name}</span>
                {!p.active && <Pill tone="red">Désactivée</Pill>}
                {p.demo && <Pill tone="violet">Démo</Pill>}
                {inPharmacy && context.schema === p.schema && <Check size={15} className="text-jamora shrink-0" aria-label="Contexte actuel" />}
              </button>
            </li>
          ))}
          {state.phase === "loading" && <li className="px-3 py-2 text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Chargement…</li>}
          {state.phase === "ready" && !state.items.length && <li className="px-3 py-3 text-sm text-slate-500" data-testid="context-empty">Aucune pharmacie ne correspond.</li>}
          {state.phase === "error" && <li className="px-3 py-3 text-sm text-red-600" role="alert">{state.error} <button type="button" className="underline" onClick={() => load(search.trim(), null)}>Réessayer</button></li>}
          {state.cursor && state.phase !== "loading" && (
            <li className="px-3 py-2"><Button variant="ghost" size="sm" className="w-full" onClick={() => load(search.trim(), state.cursor)} data-testid="context-more">Voir plus</Button></li>
          )}
        </ul>
      </ResponsivePopoverContent>
    </ResponsivePopover>
  );
}
